import React, {ChangeEvent, FocusEvent, MouseEvent} from 'react';
import classNames from 'classnames';
import {IconTypes} from "@eika/icon";
import { LinkButton } from "@eika/button";
import {validateRule} from '../util/Validations';
import Textfield from './Textfield';
import CustomCheckbox from './CustomCheckbox';
import Constant from '../../static/data/Constant';
import Message from '../Message/Message';
import {ErrorItem, InputContent, InputValidation, StringObject} from '../../types';

import './FormInput.scss';

export interface FormInputProps {
    content: InputContent[];
    formAction: any;
    submitCallback: (data: StringObject, errorList: ErrorItem[], cancel?: boolean) => void;
    submitText: string;
    cancelText?: string;
    children?: React.ReactElement;
    failed?: string;
    click?: (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>, k: string) => void;
}

const validate = ( validation?: InputValidation[], value?: string): null | InputValidation => {
    return (validation || []).find( (data: InputValidation) => {
        return !validateRule(value, data.rule);
    }) || null;
};

const submit = (props: FormInputProps, data: StringObject): void => {
    const errorList: ErrorItem[] = [];
    props.content.forEach( (input: InputContent) => {
        const invalid = validate(input.validation, data[input.name]);
        if ( !invalid ) { return; }

        errorList.push({
            name: input.name,
            message: invalid.message 
        });
    });

    if ( errorList.length ) {
        errorList.forEach( (error: ErrorItem) => {
            props.formAction.setError( error.name, {message: error.message });
        });
    }

    props.submitCallback(data, errorList);
};

const getValue = (e: ChangeEvent<HTMLInputElement> | FocusEvent<HTMLInputElement>): string => {
    return (e?.target?.value || '').trim();
};

const blur = (props: FormInputProps, content: InputContent, e: FocusEvent<HTMLInputElement>): void => {
    const { formAction: {setError, clearErrors} } = props; 
    if ( !(content.validation instanceof Array) ) { return; }

    const value = content.type !== Constant.typeCheckbox || e.target.checked ? getValue(e) : '';
    const invalid = validate( content.validation, value );

    if ( invalid ) {
        setError( content.name, {...invalid, type: invalid.rule});
    } else {
        clearErrors(content.name);
    } 
};
    
const change = (props: FormInputProps, content: InputContent, e: ChangeEvent<HTMLInputElement>): void => {
    const { formAction: {formState, clearErrors, setValue} } = props;
    const error = formState.errors[content.name];
    let value = getValue(e);

    if ( content.type === Constant.typeCheckbox && !e.target.checked ) {
        value = '';
    }

    setValue( content.name, value );
    if ( !error || !error.message ) { return; }

    const invalid = validate( content.validation, value );
    if ( !invalid ) {
        clearErrors( content.name);        
    }
};

const FormInput: React.FC<FormInputProps> = (props): React.ReactElement => {
    return (
        <form className="form-input-wrapper" noValidate onSubmit={props.formAction.handleSubmit((data: StringObject) => { submit(props, data); })}>
            { (props.content || []).map( (cnt: InputContent, i: number) => {
                return <div className={classNames('form-input-row', cnt.namespace)} key={`form-input-row-${i}`}>
                    { (cnt.type === Constant.typeText || cnt.type === Constant.typeTel || cnt.type === Constant.typeEmail || cnt.type === Constant.typePassword) && 
                        <Textfield 
                            {...props} 
                            content={cnt} 
                            blur={(e:FocusEvent<HTMLInputElement>)=> { blur(props, cnt, e); }} 
                            change={(e:ChangeEvent<HTMLInputElement>)=> { change(props, cnt, e); }}
                            click={(e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>, k='')=> { if ( props.click ) { props.click(e, k); } } }
                        /> 
                    }
                    { cnt.type === Constant.typeCheckbox && <CustomCheckbox {...props} content={cnt}  blur={(e:FocusEvent<HTMLInputElement>)=> { blur(props, cnt, e); }} change={(e:ChangeEvent<HTMLInputElement>)=> { change(props, cnt, e); }}/> }
                    { cnt.type === Constant.typeHidden && <input name={cnt.name} value={cnt.defaultValue || ''} type="hidden"/> } 
                    { cnt.type === Constant.typeElement && <>{cnt.element}</>} 
                </div>
            }) }

            { props.children }

            { !!props.failed && <Message type="error" text={props.failed} /> }
            <div className="submit-btn-holder paragraph-small -only-top">
                { !!props.cancelText && <LinkButton  icon={IconTypes.LITEN_PIL_VENSTRE} onClick={()=>{ props.submitCallback( {}, [], true);}}>
                    {props.cancelText}
                </LinkButton>}
                {/* @ts-ignore*/}
                <LinkButton buttonType='positive' iconOnRightSide type="submit" icon={IconTypes.LITEN_PIL_HOEYRE}  >{props.submitText} </LinkButton>
            </div>
        </form>
    );
};

export default FormInput;
